import { useEffect } from 'react';
import type { BoardDoc } from '../doc/BoardDoc';
import { EDITABLE_TYPES } from '../doc/schema';
import { bringForward, bringToFront, sendBackward, sendToBack } from '../doc/zorder';
import {
  copySelection,
  createLinkAt,
  cutSelection,
  looksLikeUrl,
  deleteSelection,
  duplicateSelection,
  nudgeSelection,
  pasteClipboard,
  selectAll,
  viewCenter,
  zoomBy,
  zoomTo,
  zoomToFit,
  zoomToSelection,
} from './actions';
import { importImageFiles, pickImageFiles } from './importImages';
import type { PaneStore } from './paneStore';
import { setToolSettings, toolSettings } from './toolSettings';
import { groupSelection, toggleLockSelection, ungroupSelection } from './arrange';

/** The pane that last received a pointer event; keyboard shortcuts go there. */
let activePane: PaneStore | null = null;
export const setActivePane = (p: PaneStore) => {
  activePane = p;
};

function isTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
}

export function useKeyboard(board: BoardDoc, pane: PaneStore, readOnly = false): void {
  useEffect(() => {
    // The most recently mounted pane takes the keyboard until a pointer lands on another one.
    activePane = pane;
    const ctx = { board, pane };

    const onKeyDown = (e: KeyboardEvent) => {
      if (activePane !== pane) return;
      const s = pane.getState();
      if (isTextTarget(e.target)) {
        if (e.key === 'Escape') (e.target as HTMLElement).blur();
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const NAV_KEYS = new Set(['+', '=', '-', '0', '1', '2', 'v', 'h', 'a', 'escape', '?']);
      if (readOnly && e.code !== 'Space' && !NAV_KEYS.has(key)) return;

      if (e.code === 'Space' && !e.repeat) {
        s.setSpaceHeld(true);
        e.preventDefault();
        return;
      }

      if (mod) {
        switch (key) {
          case 'z':
            if (e.shiftKey) board.undo.redo();
            else board.undo.undo();
            e.preventDefault();
            return;
          case 'y':
            board.undo.redo();
            e.preventDefault();
            return;
          case 'a':
            selectAll(ctx);
            e.preventDefault();
            return;
          case 'c':
            void copySelection(ctx);
            e.preventDefault();
            return;
          case 'x':
            void cutSelection(ctx);
            e.preventDefault();
            return;
          case 'v':
            // handled by the 'paste' event so system-clipboard images work too
            return;
          case 'd':
            void duplicateSelection(ctx);
            e.preventDefault();
            return;
          case 'g':
            if (e.shiftKey) ungroupSelection(ctx);
            else groupSelection(ctx);
            e.preventDefault();
            return;
          case 'l':
            toggleLockSelection(ctx);
            e.preventDefault();
            return;
          case "'":
            setToolSettings({ snap: !toolSettings.getState().snap });
            e.preventDefault();
            return;
          case '0':
            zoomTo(pane, 1);
            e.preventDefault();
            return;
          case '=':
          case '+':
            zoomBy(pane, 1.25);
            e.preventDefault();
            return;
          case '-':
            zoomBy(pane, 0.8);
            e.preventDefault();
            return;
          case '[':
            sendToBack(board, new Set(s.selection));
            e.preventDefault();
            return;
          case ']':
            bringToFront(board, new Set(s.selection));
            e.preventDefault();
            return;
        }
        return;
      }

      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          deleteSelection(ctx);
          e.preventDefault();
          return;
        case '?':
          window.dispatchEvent(new Event('vds:shortcuts'));
          return;
        case 'Escape':
          s.clearSelection();
          s.setEditing(null);
          s.setTool('select');
          return;
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'ArrowUp':
        case 'ArrowDown': {
          if (!s.selection.size) return;
          const d = e.shiftKey ? 10 : 1;
          const dx = e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0;
          const dy = e.key === 'ArrowUp' ? -d : e.key === 'ArrowDown' ? d : 0;
          nudgeSelection(ctx, dx, dy);
          e.preventDefault();
          return;
        }
        case '[':
          sendBackward(board, new Set(s.selection));
          return;
        case ']':
          bringForward(board, new Set(s.selection));
          return;
        case '+':
        case '=':
          zoomBy(pane, 1.25);
          return;
        case '-':
          zoomBy(pane, 0.8);
          return;
        case '0':
          zoomTo(pane, 1);
          return;
        case '1':
          zoomToFit(ctx);
          return;
        case '2':
          zoomToSelection(ctx);
          return;
      }

      switch (key) {
        case 'v':
          s.setTool('select');
          return;
        case 'h':
          s.setTool('pan');
          return;
        case 'n':
          s.setTool('note');
          return;
        case 't':
          s.setTool('title');
          return;
        case 'x':
          s.setTool('richtext');
          return;
        case 'b':
          s.setTool('table');
          return;
        case 'd':
          s.setTool('todo');
          return;
        case 'k':
          s.setTool('kanban');
          return;
        case 's':
          s.setTool('shape');
          return;
        case 'f':
          s.setTool('frame');
          return;
        case 'p':
          setToolSettings({ penMode: 'pen' });
          s.setTool('draw');
          return;
        case 'm':
          setToolSettings({ penMode: 'highlighter' });
          s.setTool('draw');
          return;
        case 'e':
          setToolSettings({ penMode: 'eraser' });
          s.setTool('draw');
          return;
        case 'i':
          void pickImageFiles().then((files) => importImageFiles(ctx, files));
          return;
        case 'enter': {
          const [only] = s.selection;
          if (only && s.selection.size === 1) {
            const item = board.index.get(only);
            if (item && EDITABLE_TYPES.has(item.type)) s.setEditing(only);
          }
          return;
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') pane.getState().setSpaceHeld(false);
    };

    const onPaste = (e: ClipboardEvent) => {
      if (activePane !== pane || isTextTarget(e.target) || readOnly) return;
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length) {
        e.preventDefault();
        void importImageFiles(ctx, files, viewCenter(pane));
        return;
      }
      const text = e.clipboardData?.getData('text/plain') ?? '';
      if (looksLikeUrl(text)) {
        e.preventDefault();
        createLinkAt(ctx, text, viewCenter(pane));
        return;
      }
      void pasteClipboard(ctx);
    };

    const onBlur = () => pane.getState().setSpaceHeld(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('paste', onPaste);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('blur', onBlur);
    };
  }, [board, pane, readOnly]);
}
